import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { ProductLine1 } from '@/app/components/icons/ProductLine1';
import { ProductLine9 } from '@/app/components/icons/ProductLine9';
import { ProductLine16 } from '@/app/components/icons/ProductLine16';
import { ProductLineAmount } from '@/app/components/icons/ProductLineAmount';
import { ProductOriginalRatio } from '@/app/components/icons/ProductOriginalRatio';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/app/components/ui/drawer';
import { useGlobalModal, useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TemplateSettings } from '@/app/types';
import { isOptionAllowed } from '@/app/utils';
import type { SupportedSettings } from '../types';
import { SettingsSelect } from './SettingsSelect';

interface SettingsSectionProps {
  settings?: TemplateSettings;
  supportedSettings: SupportedSettings;
  updateSettings: (settings: TemplateSettings) => void;
}

export const SettingsSection: FC<SettingsSectionProps> = ({ settings, supportedSettings, updateSettings }) => {
  const t = useTranslations();

  const { isSmScreen } = useIsScreen();
  const userPlan = useStore((state) => state.userInfo?.subs_plan || 'free');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');

  // 控制同一时间仅打开一个下拉框（桌面端）
  const [openedSelectId, setOpenedSelectId] = useState<'has_audio' | 'aspect_ratio' | 'sample_count' | null>(null);

  // 用于在小屏幕展示当前选中的设置
  const summaryItems = useMemo(() => {
    const items: { id: 'has_audio' | 'aspect_ratio' | 'sample_count'; label: string }[] = [];
    if (supportedSettings.hasAudio && typeof settings?.has_audio === 'boolean') {
      items.push({
        id: 'has_audio',
        label: settings.has_audio
          ? t('create.createForm.settings.audio.optionOn.text')
          : t('create.createForm.settings.audio.optionOff.text'),
      });
    }

    if (supportedSettings.aspectRatio && settings?.aspect_ratio) {
      const aspectRatio = settings.aspect_ratio;
      const label = aspectRatio === 'auto' ? t('create.createForm.settings.aspectRatio.optionAuto.text') : aspectRatio;
      items.push({
        id: 'aspect_ratio',
        label,
      });
    }

    if (supportedSettings.sampleCount && typeof settings?.sample_count === 'number') {
      items.push({
        id: 'sample_count',
        label: t('create.createForm.settings.amount.option.text', {
          count: settings.sample_count,
        }),
      });
    }

    return items;
  }, [supportedSettings, settings, t]);

  const renderAudioOption = () => {
    if (!supportedSettings.hasAudio) return null;

    const audioOptions = [
      {
        label: t('create.createForm.settings.audio.optionOn.text'),
        value: 'true',
      },
      {
        label: t('create.createForm.settings.audio.optionOff.text'),
        value: 'false',
      },
    ];

    return (
      <SettingsSelect
        title={t('create.createForm.settings.audio.title')}
        value={settings?.has_audio === true ? 'true' : 'false'}
        onChange={(value) => updateSettings({ ...(settings || {}), has_audio: value === 'true' })}
        options={audioOptions}
        icon={<BaseLineMusic />}
        open={openedSelectId === 'has_audio'}
        onOpenChange={(open) => setOpenedSelectId(open ? 'has_audio' : null)}
      />
    );
  };

  const renderAspectRatioOption = () => {
    if (!supportedSettings.aspectRatio || !supportedSettings.aspectRatio.length) return null;

    const aspectRatioIconMap: Record<string, React.ReactNode> = {
      '16:9': <ProductLine16 />,
      '9:16': <ProductLine9 />,
      '1:1': <ProductLine1 />,
      auto: <ProductOriginalRatio />,
    };

    const currentIcon = aspectRatioIconMap[settings?.aspect_ratio || '16:9'];

    // auto 的 label 使用多语言
    const options = supportedSettings.aspectRatio.map((v) => ({
      label: v === 'auto' ? t('create.createForm.settings.aspectRatio.optionAuto.text') : v,
      value: v,
    }));

    return (
      <SettingsSelect
        width={supportedSettings.aspectRatio.includes('auto') ? '158' : undefined}
        title={t('create.createForm.settings.aspectRatio.title')}
        value={settings?.aspect_ratio || ''}
        onChange={(value) => updateSettings({ ...(settings || {}), aspect_ratio: value })}
        options={options}
        icon={currentIcon}
        open={openedSelectId === 'aspect_ratio'}
        onOpenChange={(open) => setOpenedSelectId(open ? 'aspect_ratio' : null)}
      />
    );
  };

  const renderAmountOption = () => {
    if (!supportedSettings.sampleCount) return null;
    const options = ['1', '2', '3', '4'].map((value) => {
      return {
        label: value,
        value,
        showVipIcon: !isOptionAllowed('sample_count', value, 'free'),
      };
    });

    const handleChange = (value: string) => {
      if (!isOptionAllowed('sample_count', value, userPlan)) {
        openPlanDetailDialog();
        return;
      }
      updateSettings({ ...(settings || {}), sample_count: Number(value) });
    };

    return (
      <SettingsSelect
        title={t('create.createForm.settings.amount.title')}
        value={String(settings?.sample_count || 1)}
        onChange={handleChange}
        options={options}
        icon={<ProductLineAmount />}
        open={openedSelectId === 'sample_count'}
        onOpenChange={(open) => setOpenedSelectId(open ? 'sample_count' : null)}
      />
    );
  };

  if (isSmScreen) {
    if (summaryItems.length === 0) {
      return null;
    }

    return (
      <Drawer>
        <DrawerTrigger className="flex w-full items-center justify-between gap-1 overflow-hidden py-3">
          <div className="flex-1 truncate text-sm text-system-white">
            {summaryItems.map((item) => (
              <span key={item.id} className="before:mx-2 before:opacity-20 before:content-['|'] first:before:hidden">
                {item.label}
              </span>
            ))}
          </div>
          <BaseLineRight className="shrink-0 rotate-90 text-base opacity-50" />
        </DrawerTrigger>
        <DrawerContent className="px-4">
          <DrawerHeader>
            <DrawerTitle className="font-semibold text-lg">Settings</DrawerTitle>
          </DrawerHeader>
          {renderAudioOption()}
          {renderAspectRatioOption()}
          {renderAmountOption()}
          <DrawerClose className="absolute top-5 right-4">
            <BaseLineClose className="text-xl" />
          </DrawerClose>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      {renderAudioOption()}
      {renderAspectRatioOption()}
      {renderAmountOption()}
    </>
  );
};
